import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { api } from "../lib/api";
import { toast } from "sonner";

// All 25 app-level categories, grouped for the dropdown.
export const CATEGORY_GROUPS = [
    { label_en: "Housing & Utilities", label_es: "Vivienda y servicios",
      items: ["rent", "mortgage", "home_insurance", "property_tax", "utilities"] },
    { label_en: "Financial & Protection", label_es: "Finanzas y protección",
      items: ["insurance", "investment", "savings", "debt_payment", "loan_repayment"] },
    { label_en: "Income & Adjustments", label_es: "Ingresos y ajustes",
      items: ["salary", "freelance", "side_income", "reimbursement", "refunded", "cashback", "income"] },
    { label_en: "Lifestyle & Daily", label_es: "Estilo de vida",
      items: ["shopping", "groceries", "dining", "entertainment", "subscriptions", "personal_care", "transportation", "medical"] },
    { label_en: "Other", label_es: "Otros",
      items: ["transfer", "other"] },
];

const EditTransactionDialog = ({ open, onOpenChange, tx, onSaved }) => {
    const { language } = useApp();
    const [description, setDescription] = useState("");
    const [category, setCategory] = useState("");
    const [date, setDate] = useState("");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (tx) {
            setDescription(tx.merchant_name || tx.name || "");
            setCategory(tx.app_category || "other");
            setDate((tx.date || "").slice(0, 10));
            setNote(tx.note || "");
        }
    }, [tx, open]);

    const save = async () => {
        if (!tx) return;
        setSaving(true);
        try {
            const res = await api.patch(`/transactions/${tx.transaction_id}`, {
                description: description.trim(),
                category,
                date,
                note,
            });
            toast.success(language === "es" ? "Transacción actualizada" : "Transaction updated");
            onSaved?.(res.data);
            onOpenChange(false);
        } catch (err) {
            toast.error(language === "es" ? "No se pudo guardar" : "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (!tx) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[440px] bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="edit-tx-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl">
                        {language === "es" ? "Editar transacción" : "Edit transaction"}
                    </DialogTitle>
                </DialogHeader>

                <div className="grid gap-4 py-2">
                    <div className="grid gap-1.5">
                        <label className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                            {language === "es" ? "Descripción" : "Description"}
                        </label>
                        <Input
                            data-testid="edit-tx-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="h-10"
                            maxLength={200}
                        />
                    </div>

                    <div className="grid gap-1.5">
                        <label className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                            {language === "es" ? "Categoría" : "Category"}
                        </label>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger data-testid="edit-tx-category" className="h-10">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="max-h-[320px]">
                                {CATEGORY_GROUPS.map((g) => (
                                    <div key={g.label_en}>
                                        <div className="px-2 py-1.5 text-[10px] font-bold tracking-widest uppercase text-[hsl(var(--muted-foreground))]">
                                            {language === "es" ? g.label_es : g.label_en}
                                        </div>
                                        {g.items.map((c) => (
                                            <SelectItem key={c} value={c}>
                                                {t(language, `cat_${c}`)}
                                            </SelectItem>
                                        ))}
                                    </div>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="grid gap-1.5">
                        <label className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                            {language === "es" ? "Fecha" : "Date"}
                        </label>
                        <Input
                            data-testid="edit-tx-date"
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="h-10"
                        />
                    </div>

                    <div className="grid gap-1.5">
                        <label className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                            {language === "es" ? "Notas (opcional)" : "Notes (optional)"}
                        </label>
                        <Textarea
                            data-testid="edit-tx-note"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            className="min-h-[70px]"
                            maxLength={500}
                            placeholder={language === "es" ? "Añade cualquier contexto..." : "Add any context..."}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        data-testid="edit-tx-cancel"
                    >
                        {language === "es" ? "Cancelar" : "Cancel"}
                    </Button>
                    <Button
                        onClick={save}
                        disabled={saving}
                        data-testid="edit-tx-save"
                        className="bg-[hsl(var(--foreground))] text-[hsl(var(--background))] hover:opacity-90"
                    >
                        {saving
                            ? (language === "es" ? "Guardando..." : "Saving...")
                            : (language === "es" ? "Guardar" : "Save")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default EditTransactionDialog;
